import * as d3 from "d3"
import { isFuture } from "date-fns"
import { max, maxBy } from "lodash"
import { Fragment } from "react"
import { useTranslation } from "react-i18next"
import {
  Chart,
  useBarRadius,
  TRANSPARENT_OPACITY,
  getBarWidth,
  getBarOffset,
  useBandAxisMouseMovedHandler,
} from "../../../../components/chart/Chart"
import { ChartErrorStatePlaceholder } from "../../../../components/chart/ChartDataPlaceholder"
import ChartTooltipCanvas from "../../../../components/chart/ChartTooltipCanvas"
import Bar from "../../../../components/chart/components/Bar"
import ChartLabel from "../../../../components/chart/components/ChartLabel"
import InfoField from "../../../../components/chart/components/groupSummary/InfoField"
import {
  InfoSectionBaseContainer,
  InfoSectionContainer,
} from "../../../../components/chart/components/groupSummary/InfoSection"
import SvgTooltip from "../../../../components/chart/components/SvgTooltip"
import {
  CHART_Y_PADDING,
  TickDensitiesGroup,
  useAxisRenderers,
  useBandwidth,
  useChartRange,
  useXAxisTicks,
} from "../../../../components/chart/hooks"
import { DimensionProps, withResponsiveContainer } from "../../../../components/chart/withResponsiveContainer"
import Separator from "../../../../components/Separator"
import Text from "../../../../components/Text"
import { getCurrency } from "../../../../i18n/config"
import { getLocalizedMonth, getMonthName } from "../../../../utils/date"
import { formatCurrencyPerEnergy, formatEnergy } from "../../../../utils/format"
import { EnergyUnit } from "@nano-portal/shared"

type TooltipItem = {
  marketPrice: number
  production: number
  month: Date
}

type TooltipProps = {
  data: TooltipItem
  filter?: {
    marketPrice?: boolean
    production?: boolean
  }
}

function Tooltip(props: TooltipProps) {
  const { data, filter } = props
  const { t } = useTranslation("microsite")

  return (
    <>
      <InfoSectionBaseContainer>
        <Text type="headerH5">{getMonthName(data.month)}</Text>
      </InfoSectionBaseContainer>

      <Separator orientation="horizontal" decorative />

      <InfoSectionContainer>
        {!!filter?.production && (
          <InfoField
            header={t("overview_production")}
            amount={data.production}
            formatter={formatEnergy}
            circleVariant="textsAlt"
          />
        )}

        {!!filter?.marketPrice && (
          <InfoField
            header={t("market_price")}
            amount={data.marketPrice}
            formatter={formatCurrencyPerEnergy}
            circleVariant="primary"
          />
        )}
      </InfoSectionContainer>
    </>
  )
}

interface ProductionChartItem {
  marketPrice: number
  production: number
  month: number
}

type Props = DimensionProps & {
  data: ProductionChartItem[]
  isErrorState?: boolean
  filter?: {
    spotPrices?: boolean
    production?: boolean
  }
}

function ProductionChart(props: Props) {
  const { data, width, height, filter, isErrorState } = props

  const maxProduction = maxBy(data, (d) => d.production)?.production ?? 0
  const maxPrice = maxBy(data, (d) => d.marketPrice)?.marketPrice ?? 0
  const dates = data.map((d) => new Date(d.month))

  const [rangeX, rangeY] = useChartRange(width, height, data.length)

  const scaleXBand = d3.scaleBand<Date>().range(rangeX).domain(dates).paddingInner(0.1)
  const scaleY1 = d3.scaleLinear().domain([0, maxPrice]).range(rangeY)
  const scaleY2 = d3.scaleLinear().domain([0, maxProduction]).range(rangeY)

  const [bandwidth] = useBandwidth(scaleXBand)

  const { renderHorizontalLines, renderZeroHorizontalLine, renderHorizontalLinesTicks, renderHorizontalLineUnitLabel } =
    useAxisRenderers(width, [scaleY1, scaleY2], [`${getCurrency().symbol}/${EnergyUnit.kWh}`, EnergyUnit.kWh])

  const getBarRadius = useBarRadius()
  const [onMove, tooltipGroup, tooltipCoords] = useBandAxisMouseMovedHandler(
    scaleXBand,
    data,
    bandwidth,
    (value) => {
      const scaledPriceBarPosition = scaleY1(value.marketPrice / 2)
      const scaledProductionBarPosition = scaleY2(value.production / 2)

      const highestCoord = max([scaledPriceBarPosition, scaledProductionBarPosition]) ?? 0
      return highestCoord
    },
    "continuous"
  )

  const dateTicks = useXAxisTicks(dates, TickDensitiesGroup.months)
  return (
    <Chart id="production-chart" height={height} width={width}>
      {renderHorizontalLines()}
      {renderZeroHorizontalLine()}
      {renderHorizontalLineUnitLabel()}
      {renderHorizontalLinesTicks()}

      {dateTicks.map((value) => (
        <g key={value.toISOString()}>
          <ChartLabel
            date={value}
            x={(scaleXBand(value) ?? 0) + bandwidth * 0.5}
            chartHeight={height}
            chartYPadding={CHART_Y_PADDING}
            text={getLocalizedMonth(value)}
            type="small"
          />
        </g>
      ))}

      {isErrorState ? (
        <ChartErrorStatePlaceholder height={height} width={width} />
      ) : (
        <>
          {data.map((d) => {
            const month = new Date(d.month)
            const priceBarHeight = scaleY1(0) - scaleY1(Math.abs(d.marketPrice))
            const productionBarHeight = scaleY2(0) - scaleY2(Math.abs(d.production))

            return (
              <Fragment key={d.month}>
                {filter?.spotPrices && (
                  <g opacity={isFuture(month) ? TRANSPARENT_OPACITY : 1}>
                    <Bar
                      startX={(scaleXBand(month) ?? 0) + getBarOffset(bandwidth, "large")}
                      startY={scaleY1(0)}
                      width={getBarWidth(bandwidth, "large")}
                      height={priceBarHeight}
                      fill="primary"
                      orientation={d.marketPrice < 0 ? "down" : "up"}
                      radius={getBarRadius("large")}
                    />
                  </g>
                )}
                {filter?.production && (
                  <g opacity={isFuture(month) ? TRANSPARENT_OPACITY : 1}>
                    <Bar
                      startX={(scaleXBand(month) ?? 0) + getBarOffset(bandwidth, "normal")}
                      startY={scaleY2(0)}
                      width={getBarWidth(bandwidth, "normal")}
                      height={productionBarHeight}
                      fill="textsAlt"
                      orientation={d.production < 0 ? "down" : "up"}
                      radius={getBarRadius("normal")}
                    />
                  </g>
                )}
              </Fragment>
            )
          })}

          {(filter?.production || filter?.spotPrices) && (
            <>
              <ChartTooltipCanvas width={width} height={height} onChangeX={onMove} />
              {tooltipCoords !== null && tooltipGroup && (
                <SvgTooltip maxHeight={height} maxWidth={width} baseX={tooltipCoords[0]} baseY={tooltipCoords[1]}>
                  <Tooltip
                    data={{ ...tooltipGroup, month: new Date(tooltipGroup.month) }}
                    filter={{ marketPrice: filter?.spotPrices, production: filter?.production }}
                  />
                </SvgTooltip>
              )}
            </>
          )}
        </>
      )}
    </Chart>
  )
}

export const ProductionChartResponsive = withResponsiveContainer(ProductionChart)
